import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { SharedService } from '../../../services/shared.service';
import { MiscReportService } from '../../../services/misc-report.service';

export interface PaymentMethodStatSummary {
  cardCount: number;
  bankAccountCount: number;
  totalCount: number;
}

export interface PaymentMethodStatByDate {
  date: string;
  cardCount: number;
  bankAccountCount: number;
  totalCount: number;
}

export interface PaymentMethodStatByBusiness {
  businessName: string;
  cardCount: number;
  bankAccountCount: number;
  totalCount: number;
}

export interface PaymentMethodWeeklyBar {
  label: string;
  cardCount: number;
  bankAccountCount: number;
  totalCount: number;
  cardPct: number;
  bankPct: number;
}

export interface LinkChannelCounts {
  totalCount: number;
  completedCount: number;
  pendingCount: number;
  canceledCount: number;
  completedRate: number;
  pendingRate: number;
}

export interface LinkSetupSummary {
  totalCount: number;
  completedCount: number;
  pendingCount: number;
  canceledCount: number;
  completedRate: number;
  pendingRate: number;
  email: LinkChannelCounts;
  sms: LinkChannelCounts;
  portal: LinkChannelCounts;
  unknown: LinkChannelCounts;
}

export interface LinkSetupByDate {
  date: string;
  totalCount: number;
  completedCount: number;
  pendingCount: number;
  canceledCount: number;
  completedRate: number;
  pendingRate: number;
  emailTotalCount: number;
  emailCompletedCount: number;
  emailPendingCount: number;
  emailCanceledCount: number;
  smsTotalCount: number;
  smsCompletedCount: number;
  smsPendingCount: number;
  smsCanceledCount: number;
  portalTotalCount: number;
  portalCompletedCount: number;
  portalPendingCount: number;
  portalCanceledCount: number;
  unknownTotalCount: number;
  unknownCompletedCount: number;
  unknownPendingCount: number;
  unknownCanceledCount: number;
  completedPct: number;
  pendingPct: number;
  canceledPct: number;
  emailCompletedPct: number;
  smsCompletedPct: number;
}

export interface LinkSetupByHour {
  hour: number;
  hourLabel: string;
  totalCount: number;
  completedCount: number;
  pendingCount: number;
  canceledCount: number;
  completedPct: number;
  pendingPct: number;
  canceledPct: number;
  barHeightPct: number;
}

@Component({
  selector: 'app-misc-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.css']
})
export class MiscPaymentMethodsComponent implements OnInit {
  fromDate: Date = null as any;
  toDate: Date = null as any;
  loading = false;
  errorMessage = '';
  linkErrorMessage = '';
  loaded = false;
  linkLoaded = false;
  activeTab: 'settingResult' | 'linkPerformance' = 'settingResult';

  summary: PaymentMethodStatSummary = {
    cardCount: 0,
    bankAccountCount: 0,
    totalCount: 0
  };
  byDate: PaymentMethodStatByDate[] = [];
  byBusiness: PaymentMethodStatByBusiness[] = [];
  weeklyBars: PaymentMethodWeeklyBar[] = [];
  activeDays: PaymentMethodStatByDate[] = [];
  dailyMax = 1;

  linkSummary: LinkSetupSummary = this.emptyLinkSummary();
  linkByDate: LinkSetupByDate[] = [];
  linkTodayDate = '';
  linkTodayByHour: LinkSetupByHour[] = [];
  linkTodayHourMax = 1;
  linkTodayTotal = 0;
  linkDailyMax = 1;
  linkChannelDailyMax = 1;

  constructor(
    private sharedService: SharedService,
    private miscReportService: MiscReportService
  ) { }

  ngOnInit(): void {
    this.applyDefaultDateRange();
    this.load();
  }

  selectTab(tab: 'settingResult' | 'linkPerformance'): void {
    this.activeTab = tab;
  }

  applyDefaultDateRange(): void {
    const today = new Date();
    const to = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const from = new Date(to.getFullYear(), to.getMonth() - 1, 1);
    this.fromDate = from;
    this.toDate = to;
  }

  load(): void {
    this.errorMessage = '';
    this.linkErrorMessage = '';
    if (!this.fromDate || !this.toDate) {
      this.errorMessage = 'From date and to date are required.';
      return;
    }

    const fromIso = this.toIsoDate(this.fromDate);
    const toIso = this.toIsoDate(this.toDate);
    if (!fromIso || !toIso) {
      this.errorMessage = 'Invalid from date or to date.';
      return;
    }
    if (toIso < fromIso) {
      this.errorMessage = 'To date cannot be before from date.';
      return;
    }

    const userId = this.sharedService.getUserId();
    if (!userId) {
      this.errorMessage = 'User is not signed in.';
      return;
    }

    this.loading = true;
    const self = this;
    forkJoin([
      this.miscReportService.getPaymentMethodStatistics(userId, fromIso, toIso),
      this.miscReportService.getPaymentLinkSetupStatistics(userId, fromIso, toIso)
    ]).subscribe(
      function (results) {
        self.loading = false;
        const methodsResult = results[0];
        const linksResult = results[1];

        if (!methodsResult || methodsResult.success !== true || !methodsResult.data
          || typeof methodsResult.data === 'string') {
          self.loaded = false;
          self.errorMessage = self.resolveErrorMessage(
            methodsResult,
            'Failed to load payment method statistics.'
          );
        } else {
          self.applyMethodsResult(methodsResult.data);
          self.loaded = true;
          self.errorMessage = '';
        }

        if (!linksResult || linksResult.success !== true || !linksResult.data
          || typeof linksResult.data === 'string') {
          self.linkLoaded = false;
          self.linkSummary = self.emptyLinkSummary();
          self.linkByDate = [];
          self.linkTodayByHour = [];
          self.linkTodayDate = '';
          self.linkTodayTotal = 0;
          self.linkErrorMessage = self.resolveErrorMessage(
            linksResult,
            'Failed to load payment link setup statistics.'
          );
        } else {
          self.applyLinkResult(linksResult.data);
          self.linkLoaded = true;
          self.linkErrorMessage = '';
        }
      },
      function () {
        self.loading = false;
        self.loaded = false;
        self.linkLoaded = false;
        self.errorMessage = 'Failed to load payment statistics.';
        self.linkErrorMessage = '';
      }
    );
  }

  private emptyChannel(): LinkChannelCounts {
    return {
      totalCount: 0,
      completedCount: 0,
      pendingCount: 0,
      canceledCount: 0,
      completedRate: 0,
      pendingRate: 0
    };
  }

  private emptyLinkSummary(): LinkSetupSummary {
    return {
      totalCount: 0,
      completedCount: 0,
      pendingCount: 0,
      canceledCount: 0,
      completedRate: 0,
      pendingRate: 0,
      email: this.emptyChannel(),
      sms: this.emptyChannel(),
      portal: this.emptyChannel(),
      unknown: this.emptyChannel()
    };
  }

  private applyMethodsResult(data: any): void {
    const summary = data.summary || data.Summary || {};
    this.summary = {
      cardCount: Number(summary.cardCount != null ? summary.cardCount : summary.CardCount) || 0,
      bankAccountCount: Number(
        summary.bankAccountCount != null ? summary.bankAccountCount : summary.BankAccountCount
      ) || 0,
      totalCount: Number(summary.totalCount != null ? summary.totalCount : summary.TotalCount) || 0
    };

    const rawDates = data.byDate || data.ByDate || [];
    this.byDate = [];
    for (let i = 0; i < rawDates.length; i++) {
      const row = rawDates[i];
      this.byDate.push({
        date: row.date || row.Date || '',
        cardCount: Number(row.cardCount != null ? row.cardCount : row.CardCount) || 0,
        bankAccountCount: Number(
          row.bankAccountCount != null ? row.bankAccountCount : row.BankAccountCount
        ) || 0,
        totalCount: Number(row.totalCount != null ? row.totalCount : row.TotalCount) || 0
      });
    }

    const rawBiz = data.byBusinessName || data.ByBusinessName || [];
    this.byBusiness = [];
    for (let j = 0; j < rawBiz.length; j++) {
      const b = rawBiz[j];
      this.byBusiness.push({
        businessName: b.businessName || b.BusinessName || '',
        cardCount: Number(b.cardCount != null ? b.cardCount : b.CardCount) || 0,
        bankAccountCount: Number(
          b.bankAccountCount != null ? b.bankAccountCount : b.BankAccountCount
        ) || 0,
        totalCount: Number(b.totalCount != null ? b.totalCount : b.TotalCount) || 0
      });
    }

    this.activeDays = this.byDate.filter(function (d) {
      return d.totalCount > 0;
    });

    this.dailyMax = 1;
    for (let k = 0; k < this.byDate.length; k++) {
      if (this.byDate[k].totalCount > this.dailyMax) {
        this.dailyMax = this.byDate[k].totalCount;
      }
    }

    this.weeklyBars = this.buildWeeklyBars(this.byDate);
  }

  private applyLinkResult(data: any): void {
    const summary = data.summary || data.Summary || {};
    this.linkSummary = {
      totalCount: this.num(summary.totalCount, summary.TotalCount),
      completedCount: this.num(summary.completedCount, summary.CompletedCount),
      pendingCount: this.num(summary.pendingCount, summary.PendingCount),
      canceledCount: this.num(summary.canceledCount, summary.CanceledCount),
      completedRate: this.num(summary.completedRate, summary.CompletedRate),
      pendingRate: this.num(summary.pendingRate, summary.PendingRate),
      email: this.readChannel(summary.email || summary.Email),
      sms: this.readChannel(summary.sms || summary.Sms),
      portal: this.readChannel(summary.portal || summary.Portal),
      unknown: this.readChannel(summary.unknown || summary.Unknown)
    };

    const rawDates = data.byDate || data.ByDate || [];
    this.linkByDate = [];
    this.linkDailyMax = 1;
    this.linkChannelDailyMax = 1;

    for (let i = 0; i < rawDates.length; i++) {
      const row = rawDates[i];
      const total = this.num(row.totalCount, row.TotalCount);
      const completed = this.num(row.completedCount, row.CompletedCount);
      const pending = this.num(row.pendingCount, row.PendingCount);
      const canceled = this.num(row.canceledCount, row.CanceledCount);
      const emailCompleted = this.num(row.emailCompletedCount, row.EmailCompletedCount);
      const smsCompleted = this.num(row.smsCompletedCount, row.SmsCompletedCount);

      if (total > this.linkDailyMax) {
        this.linkDailyMax = total;
      }
      if (emailCompleted > this.linkChannelDailyMax) {
        this.linkChannelDailyMax = emailCompleted;
      }
      if (smsCompleted > this.linkChannelDailyMax) {
        this.linkChannelDailyMax = smsCompleted;
      }

      this.linkByDate.push({
        date: row.date || row.Date || '',
        totalCount: total,
        completedCount: completed,
        pendingCount: pending,
        canceledCount: canceled,
        completedRate: this.num(row.completedRate, row.CompletedRate),
        pendingRate: this.num(row.pendingRate, row.PendingRate),
        emailTotalCount: this.num(row.emailTotalCount, row.EmailTotalCount),
        emailCompletedCount: emailCompleted,
        emailPendingCount: this.num(row.emailPendingCount, row.EmailPendingCount),
        emailCanceledCount: this.num(row.emailCanceledCount, row.EmailCanceledCount),
        smsTotalCount: this.num(row.smsTotalCount, row.SmsTotalCount),
        smsCompletedCount: smsCompleted,
        smsPendingCount: this.num(row.smsPendingCount, row.SmsPendingCount),
        smsCanceledCount: this.num(row.smsCanceledCount, row.SmsCanceledCount),
        portalTotalCount: this.num(row.portalTotalCount, row.PortalTotalCount),
        portalCompletedCount: this.num(row.portalCompletedCount, row.PortalCompletedCount),
        portalPendingCount: this.num(row.portalPendingCount, row.PortalPendingCount),
        portalCanceledCount: this.num(row.portalCanceledCount, row.PortalCanceledCount),
        unknownTotalCount: this.num(row.unknownTotalCount, row.UnknownTotalCount),
        unknownCompletedCount: this.num(row.unknownCompletedCount, row.UnknownCompletedCount),
        unknownPendingCount: this.num(row.unknownPendingCount, row.UnknownPendingCount),
        unknownCanceledCount: this.num(row.unknownCanceledCount, row.UnknownCanceledCount),
        completedPct: this.sharePct(completed, total),
        pendingPct: this.sharePct(pending, total),
        canceledPct: this.sharePct(canceled, total),
        emailCompletedPct: 0,
        smsCompletedPct: 0
      });
    }

    for (let n = 0; n < this.linkByDate.length; n++) {
      this.linkByDate[n].emailCompletedPct = this.heightPct(
        this.linkByDate[n].emailCompletedCount,
        this.linkChannelDailyMax
      );
      this.linkByDate[n].smsCompletedPct = this.heightPct(
        this.linkByDate[n].smsCompletedCount,
        this.linkChannelDailyMax
      );
    }

    this.applyTodayByHour(data);
  }

  private applyTodayByHour(data: any): void {
    this.linkTodayDate = data.todayDate || data.TodayDate || '';
    const rawHours = data.todayByHour || data.TodayByHour || [];
    this.linkTodayByHour = [];
    this.linkTodayHourMax = 1;
    this.linkTodayTotal = 0;

    for (let i = 0; i < rawHours.length; i++) {
      const row = rawHours[i];
      const completed = this.num(row.completedCount, row.CompletedCount);
      if (completed > this.linkTodayHourMax) {
        this.linkTodayHourMax = completed;
      }
      this.linkTodayTotal += completed;
      const hour = this.num(row.hour, row.Hour);
      this.linkTodayByHour.push({
        hour: hour,
        hourLabel: row.hourLabel || row.HourLabel || (('0' + hour).slice(-2) + ':00'),
        totalCount: completed,
        completedCount: completed,
        pendingCount: 0,
        canceledCount: 0,
        completedPct: 100,
        pendingPct: 0,
        canceledPct: 0,
        barHeightPct: 0
      });
    }

    for (let n = 0; n < this.linkTodayByHour.length; n++) {
      this.linkTodayByHour[n].barHeightPct = this.heightPct(
        this.linkTodayByHour[n].completedCount,
        this.linkTodayHourMax
      );
    }
  }

  private readChannel(raw: any): LinkChannelCounts {
    if (!raw) {
      return this.emptyChannel();
    }
    return {
      totalCount: this.num(raw.totalCount, raw.TotalCount),
      completedCount: this.num(raw.completedCount, raw.CompletedCount),
      pendingCount: this.num(raw.pendingCount, raw.PendingCount),
      canceledCount: this.num(raw.canceledCount, raw.CanceledCount),
      completedRate: this.num(raw.completedRate, raw.CompletedRate),
      pendingRate: this.num(raw.pendingRate, raw.PendingRate)
    };
  }

  private num(a: any, b: any): number {
    const v = a != null ? a : b;
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  }

  private sharePct(part: number, total: number): number {
    if (!total || total <= 0) {
      return 0;
    }
    return Math.round((part / total) * 100);
  }

  private heightPct(value: number, max: number): number {
    if (!value || max <= 0) {
      return 0;
    }
    return Math.max(2, Math.round((value / max) * 100));
  }

  private buildWeeklyBars(rows: PaymentMethodStatByDate[]): PaymentMethodWeeklyBar[] {
    const map: { [key: string]: PaymentMethodWeeklyBar } = {};
    const keys: string[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.date) {
        continue;
      }
      const key = this.weekStartIso(row.date);
      if (!map[key]) {
        map[key] = {
          label: 'w/o ' + key.slice(5),
          cardCount: 0,
          bankAccountCount: 0,
          totalCount: 0,
          cardPct: 0,
          bankPct: 0
        };
        keys.push(key);
      }
      map[key].cardCount += row.cardCount;
      map[key].bankAccountCount += row.bankAccountCount;
      map[key].totalCount += row.totalCount;
    }

    keys.sort();
    let max = 1;
    for (let j = 0; j < keys.length; j++) {
      if (map[keys[j]].totalCount > max) {
        max = map[keys[j]].totalCount;
      }
    }

    const bars: PaymentMethodWeeklyBar[] = [];
    for (let n = 0; n < keys.length; n++) {
      const bar = map[keys[n]];
      bar.cardPct = Math.round((bar.cardCount / max) * 100);
      bar.bankPct = Math.round((bar.bankAccountCount / max) * 100);
      bars.push(bar);
    }
    return bars;
  }

  private weekStartIso(isoDate: string): string {
    const parts = isoDate.split('-');
    if (parts.length !== 3) {
      return isoDate;
    }
    const d = new Date(
      Number(parts[0]),
      Number(parts[1]) - 1,
      Number(parts[2]),
      12, 0, 0
    );
    const day = d.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + mondayOffset);
    return this.toIsoDate(d);
  }

  dailyHeightPct(totalCount: number): number {
    return this.heightPct(totalCount, this.dailyMax);
  }

  linkStackHeightPct(totalCount: number): number {
    return this.heightPct(totalCount, this.linkDailyMax);
  }

  channelSharePct(channelTotal: number): number {
    if (!this.linkSummary || !this.linkSummary.totalCount) {
      return 0;
    }
    return Math.round((channelTotal / this.linkSummary.totalCount) * 100);
  }

  outcomePct(part: number): number {
    if (!this.linkSummary || !this.linkSummary.totalCount) {
      return 0;
    }
    return Math.round((part / this.linkSummary.totalCount) * 100);
  }

  channelCompletedBarPct(channel: LinkChannelCounts): number {
    if (!channel || !channel.totalCount) {
      return 0;
    }
    return Math.round((channel.completedCount / channel.totalCount) * 100);
  }

  channelPendingBarPct(channel: LinkChannelCounts): number {
    if (!channel || !channel.totalCount) {
      return 0;
    }
    return Math.round((channel.pendingCount / channel.totalCount) * 100);
  }

  channelCanceledBarPct(channel: LinkChannelCounts): number {
    if (!channel || !channel.totalCount) {
      return 0;
    }
    return Math.round((channel.canceledCount / channel.totalCount) * 100);
  }

  shortDate(iso: string): string {
    if (!iso || iso.length < 10) {
      return iso || '';
    }
    return iso.slice(5);
  }

  methodMixCardPct(): number {
    if (!this.summary || !this.summary.totalCount) {
      return 0;
    }
    return Math.round((this.summary.cardCount / this.summary.totalCount) * 100);
  }

  methodMixBankPct(): number {
    if (!this.summary || !this.summary.totalCount) {
      return 0;
    }
    return Math.round((this.summary.bankAccountCount / this.summary.totalCount) * 100);
  }

  private resolveErrorMessage(
    result: { success?: boolean; data?: any; message?: string },
    fallback: string
  ): string {
    if (result && typeof result.data === 'string' && result.data) {
      return result.data;
    }
    if (result && result.message) {
      return result.message;
    }
    return fallback;
  }

  private toIsoDate(d: Date): string {
    if (!d || isNaN(d.getTime())) {
      return '';
    }
    const y = d.getFullYear();
    const m = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return y + '-' + m + '-' + day;
  }
}
